import { useParams, Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { useCategory } from "@/lib/queries";
import { getIcon } from "@/lib/icons";
import { breadcrumbSchema } from "@/lib/schema";
import FinalCta from "@/sections/FinalCta";
import NotFound from "@/pages/NotFound";

export default function CategoryPage() {
  const { slug } = useParams();
  const { data, isLoading, isError } = useCategory(slug);

  if (isError) return <NotFound />;
  if (isLoading || !data) {
    return <div className="flex min-h-screen items-center justify-center text-neutral-500">Yükleniyor…</div>;
  }

  const { category, services } = data;
  const Icon = getIcon(category.icon);
  const crumbs = [{ label: "Ana Sayfa", to: "/" }, { label: "Hizmetler", to: "/hizmetler" }, { label: category.name }];

  return (
    <>
      <Seo
        title={`${category.name} | Meguiar's İzmit`}
        description={category.short}
        image={category.image}
        canonicalPath={`/hizmetler/kategori/${slug}`}
        jsonLd={breadcrumbSchema(crumbs)}
      />
      <PageHero overline="Hizmet Kategorisi" title={category.name} subtitle={category.short} image={category.image} breadcrumbs={crumbs} />

      <section className="obsidian py-16 sm:py-24">
        <div className="container-x">
          {services?.length > 0 && (
            <>
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center border border-gold/30 bg-gold/10 text-gold"><Icon className="h-5 w-5" /></span>
                <h2 className="font-display text-2xl font-medium text-white sm:text-3xl">Öne çıkan hizmetler</h2>
              </div>
              <div className="mt-8 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {services.map((s, i) => (
                  <Reveal key={s.id} delay={(i % 3) * 0.07}>
                    <Link
                      to={`/hizmetler/${s.slug}`}
                      data-testid={`service-link-${s.slug}`}
                      className="group flex h-full flex-col overflow-hidden border border-white/10 bg-neutral-900 transition-colors hover:border-gold/40"
                    >
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img src={`${s.card_image || s.hero_image}?w=640`} alt={s.name} loading="lazy" decoding="async" className="h-full w-full object-cover grayscale-[0.2] transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0" />
                        <div className="absolute inset-0 bg-gradient-to-t from-neutral-900 via-neutral-900/30 to-transparent" />
                      </div>
                      <div className="flex flex-1 flex-col p-5">
                        <h3 className="font-display text-lg font-medium text-white transition-colors group-hover:text-gold">{s.name}</h3>
                        <p className="mt-2 flex-1 text-sm text-neutral-400 line-clamp-2">{s.excerpt}</p>
                        <span className="mt-4 inline-flex items-center gap-1.5 font-oswald text-xs uppercase tracking-[0.15em] text-gold">Detaylar <ArrowUpRight className="h-3.5 w-3.5" /></span>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </>
          )}

          {category.sub_services?.length > 0 && (
            <div className="mt-16 border border-white/10 bg-black p-8">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center border border-gold/30 bg-gold/10 text-gold"><Icon className="h-5 w-5" /></span>
                <h2 className="font-display text-xl font-medium text-white">{category.name} kapsamındaki tüm uygulamalar</h2>
              </div>
              <div className="mt-6 flex flex-wrap gap-2.5">
                {category.sub_services.map((s) => (
                  <span key={s} className="border border-white/10 bg-white/[0.02] px-4 py-2 text-sm text-neutral-300">{s}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
      <FinalCta />
    </>
  );
}
